import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Store,
  Image,
  Moon,
  Sun,
  Palette,
  Smartphone,
  RotateCcw,
  Hash,
} from "lucide-react";
import { useConfirm } from "@/contexts/confirm-context";

interface ShopSettings {
  shopName: string;
  shopAddress: string;
  phone1: string;
  phone2: string;
  receiptNote: string;
  logo: string;
  theme: "light" | "dark" | "system";
  primaryColor: string;
}

const DEFAULT_SETTINGS: ShopSettings = {
  shopName: "پێشانگای قەندیل",
  shopAddress: "",
  phone1: "",
  phone2: "",
  receiptNote: "",
  logo: "",
  theme: "system",
  primaryColor: "blue",
};

const COLORS = [
  { name: "شین", value: "blue", hslLight: "221.2 83.2% 53.3%", hslDark: "217.2 91.2% 59.8%" },
  { name: "سەوز", value: "green", hslLight: "142.1 76.2% 36.3%", hslDark: "142.1 70.6% 45.3%" },
  { name: "سوور", value: "red", hslLight: "0 72.2% 50.6%", hslDark: "0 72.2% 50.6%" },
  { name: "مۆر", value: "purple", hslLight: "262.1 83.3% 57.8%", hslDark: "263.4 70% 50.4%" },
  { name: "نارنجی", value: "orange", hslLight: "24.6 95% 53.1%", hslDark: "20.5 90.2% 48.2%" },
];

interface SettingsPageProps {
  embedded?: boolean;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "users" | "settings") => void;
}

export function SettingsPage({ embedded = false, onNavigate }: SettingsPageProps) {
  const { confirm, alert } = useConfirm();
  const [settings, setSettings] = useState<ShopSettings>(() => {
    // Try to load theme/color preferences locally to avoid flicker
    const localTheme = localStorage.getItem('qandil-theme');
    const localColor = localStorage.getItem('qandil-color');

    // For other settings, we use defaults until API loads
    return {
      ...DEFAULT_SETTINGS,
      theme: (localTheme as "light" | "dark" | "system") || DEFAULT_SETTINGS.theme,
      primaryColor: localColor || DEFAULT_SETTINGS.primaryColor,
    };
  });
  const [isResettingInvoice, setIsResettingInvoice] = useState(false);
  const [invoiceResetSuccess, setInvoiceResetSuccess] = useState(false);
  const [saved, setSaved] = useState(false);
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState<number | null>(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [newInvoiceNumber, setNewInvoiceNumber] = useState("");
  const [isUpdatingInvoice, setIsUpdatingInvoice] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const getApiUrl = () => {
    return import.meta.env.VITE_KV_API_URL || 'https://qandil-realtime-api.qandil.workers.dev';
  };

  // Load settings from API
  const fetchSettings = async () => {
    setIsLoadingSettings(true);
    try {
      const token = localStorage.getItem('auth-token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Merge with local theme preferences (theme and primaryColor stay in localStorage)
        const localTheme = localStorage.getItem('qandil-theme') || 'system';
        let localColor = localStorage.getItem('qandil-color') || 'blue';

        // Validate color - only allow blue, green, red, purple, orange
        const validColors = ['blue', 'green', 'red', 'purple', 'orange'];
        if (!validColors.includes(localColor)) {
          localColor = 'blue';
          localStorage.setItem('qandil-color', 'blue');
        }

        setSettings({
          ...data,
          theme: localTheme as "light" | "dark" | "system",
          primaryColor: localColor
        });
      } else {
        // If no settings in API, check localStorage for migration
        const oldSettings = localStorage.getItem('qandil-settings');
        if (oldSettings) {
          try {
            const parsed = JSON.parse(oldSettings);
            // Migrate to API
            const { theme, primaryColor, ...shopSettings } = parsed;
            await fetch(`${getApiUrl()}/settings`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(shopSettings),
            });
            // Save theme/color to new keys
            localStorage.setItem('qandil-theme', theme || 'system');
            localStorage.setItem('qandil-color', primaryColor || 'blue');
            // Remove old key
            localStorage.removeItem('qandil-settings');
            setSettings(parsed);
          } catch (e) {
            setSettings(DEFAULT_SETTINGS);
          }
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  // Save settings to API
  const saveSettings = async (newSettings: ShopSettings) => {
    setIsSavingSettings(true);
    try {
      const token = localStorage.getItem('auth-token') || import.meta.env.VITE_KV_TOKEN;

      // Save shop info to API (not theme/color)
      const { theme, primaryColor, ...shopSettings } = newSettings;
      const response = await fetch(`${getApiUrl()}/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(shopSettings),
      });

      if (response.ok) {
        // Save theme preferences locally
        localStorage.setItem('qandil-theme', theme);
        localStorage.setItem('qandil-color', primaryColor);
        setSettings(newSettings);
      } else {
        console.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setIsSavingSettings(false);
    }
  };

  const fetchCurrentInvoiceNumber = async () => {
    setIsLoadingInvoice(true);
    try {
      const token = localStorage.getItem('auth-token') || import.meta.env.VITE_KV_TOKEN;

      // سەرەتا هەوڵ بدە لە API وەریبگریت
      try {
        const response = await fetch(`${getApiUrl()}/invoice-number`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setCurrentInvoiceNumber(data.invoiceNumber || 0);
          setNewInvoiceNumber((data.invoiceNumber || 0).toString());
          setIsLoadingInvoice(false);
          return;
        }
      } catch (error) {
        console.log('API not available, fetching from payments history');
      }

      // ئەگەر API کار نەکرد، لە مێژووی پارەدانەکان بیگرە
      const paymentsResponse = await fetch(`${getApiUrl()}/payments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (paymentsResponse.ok) {
        const payments = await paymentsResponse.json();
        // دۆزینەوەی گەورەترین ژمارەی وەصڵ
        let maxInvoiceNumber = 0;
        payments.forEach((payment: any) => {
          if (payment.invoiceNumber && payment.invoiceNumber > maxInvoiceNumber) {
            maxInvoiceNumber = payment.invoiceNumber;
          }
        });
        setCurrentInvoiceNumber(maxInvoiceNumber);
        setNewInvoiceNumber(maxInvoiceNumber.toString());
      } else {
        setCurrentInvoiceNumber(0);
        setNewInvoiceNumber("0");
      }
    } catch (error) {
      console.error('Error fetching invoice number:', error);
      setCurrentInvoiceNumber(0);
      setNewInvoiceNumber("0");
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  const updateInvoiceNumber = async () => {
    const newNumber = parseInt(newInvoiceNumber);
    if (isNaN(newNumber) || newNumber < 0) {
      await alert({
        title: "هەڵە",
        description: "تکایە ژمارەیەکی دروست بنووسە",
        variant: "error"
      });
      return;
    }

    const confirmed = await confirm({
      title: "گۆڕینی ژمارەی وەصڵ",
      description: `دڵنیایت لە گۆڕینی ژمارەی وەصڵ بۆ ${newNumber}؟`,
      variant: "warning",
      confirmText: "بەڵێ، بیگۆڕە",
      cancelText: "نەخێر"
    });
    if (!confirmed) return;

    setIsUpdatingInvoice(true);
    try {
      const token = localStorage.getItem('auth-token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/invoice-number/set`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceNumber: newNumber }),
      });

      if (response.ok) {
        setCurrentInvoiceNumber(newNumber);
        await alert({
          title: "سەرکەوتوو",
          description: "ژمارەی وەصڵ بە سەرکەوتوویی نوێ کرایەوە",
          variant: "info"
        });
      } else {
        await alert({
          title: "هەڵە",
          description: "هەڵە لە نوێکردنەوەی ژمارەی وەصڵ",
          variant: "error"
        });
      }
    } catch (error) {
      console.error('Error updating invoice number:', error);
      await alert({
        title: "هەڵە",
        description: "هەڵە لە نوێکردنەوەی ژمارەی وەصڵ",
        variant: "error"
      });
    } finally {
      setIsUpdatingInvoice(false);
    }
  };

  const resetInvoiceNumber = async () => {
    const confirmed = await confirm({
      title: "سفرکردنەوەی ژمارەی وەصڵ",
      description: "دڵنیایت لە سفرکردنەوەی ژمارەی وەصڵ؟ ئەم کارە ناگەڕێتەوە.",
      variant: "destructive",
      confirmText: "بەڵێ، سفری بکە",
      cancelText: "نەخێر"
    });
    if (!confirmed) return;

    setIsResettingInvoice(true);
    try {
      const token = localStorage.getItem('auth-token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/invoice-number/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setInvoiceResetSuccess(true);
        setCurrentInvoiceNumber(0);
        setNewInvoiceNumber("0");
        setTimeout(() => setInvoiceResetSuccess(false), 3000);
      } else {
        await alert({
          title: "هەڵە",
          description: "هەڵە لە سفرکردنەوەی ژمارەی وەصڵ",
          variant: "error"
        });
      }
    } catch (error) {
      console.error('Error resetting invoice number:', error);
      await alert({
        title: "هەڵە",
        description: "هەڵە لە سفرکردنەوەی ژمارەی وەصڵ",
        variant: "error"
      });
    } finally {
      setIsResettingInvoice(false);
    }
  };

  // Load invoice number on mount
  useEffect(() => {
    fetchCurrentInvoiceNumber();
    fetchSettings();
  }, []);

  // Debounce timer for saving settings to API
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update settings (with debounced auto-save to API)
  const updateSettings = (newSettings: ShopSettings) => {
    // Update local state instantly for zero-latency UI feedback
    setSettings(newSettings);
    
    // Save theme/color locally immediately
    localStorage.setItem('qandil-theme', newSettings.theme);
    localStorage.setItem('qandil-color', newSettings.primaryColor);

    // Clear any pending save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce API POST request by 500ms
    saveTimeoutRef.current = setTimeout(() => {
      saveSettings(newSettings);
    }, 500);
  };

  useEffect(() => {
    const root = document.documentElement;
    const isDark = settings.theme === "dark" ||
      (settings.theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);

    if (settings.theme === "dark") {
      root.classList.add("dark");
    } else if (settings.theme === "light") {
      root.classList.remove("dark");
    } else {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    }

    const selectedColor = COLORS.find((c) => c.value === settings.primaryColor);
    if (selectedColor) {
      const hsl = isDark ? selectedColor.hslDark : selectedColor.hslLight;
      root.style.setProperty("--primary", `hsl(${hsl})`);
      if (isDark) {
        root.style.setProperty("--primary-foreground", "hsl(0 0% 9%)");
      } else {
        root.style.setProperty("--primary-foreground", "hsl(0 0% 98%)");
      }
    }
  }, [settings.theme, settings.primaryColor]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert({
          title: "قەبارەی وێنە زۆر گەورەیە",
          description: "تکایە وێنەیەکی بچووکتر هەڵبژێرە (کەمتر لە ٢ مێگابایت)",
          variant: "warning"
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        updateSettings({ ...settings, logo: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center py-12" dir="rtl">
        <div className="text-center">
          <div className="text-muted-foreground">چاوەڕوان بە...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir="rtl">
      {/* Shop Info & Logo Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            زانیاری پێشانگا
          </CardTitle>
          <CardDescription>زانیاری سەرەکی پێشانگا و لۆگۆ</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4 md:gap-6">
            {/* Logo */}
            <div className="flex flex-col items-center gap-2 md:items-start">
              <div className="h-20 w-20 md:h-24 md:w-24 rounded-lg border-2 border-dashed flex items-center justify-center bg-muted/30 overflow-hidden">
                {settings.logo ? (
                  <img src={settings.logo} alt="Logo" className="h-full w-full object-contain" />
                ) : (
                  <Image className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <Button variant="outline" size="sm" asChild>
                    <span>
                      هەڵبژاردنی وێنە
                      <Image className="h-4 w-4 mr-2" />
                    </span>
                  </Button>
                </Label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                {settings.logo && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateSettings({ ...settings, logo: "" })}
                    className="text-destructive h-8"
                  >
                    سڕینەوە
                  </Button>
                )}
              </div>
            </div>
            {/* Info Fields */}
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>ناوی پێشانگا</Label>
                  <Input
                    value={settings.shopName}
                    onChange={(e) => updateSettings({ ...settings, shopName: e.target.value })}
                    placeholder="ناوی پێشانگا"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ناونیشانی پێشانگا</Label>
                  <Input
                    value={settings.shopAddress}
                    onChange={(e) => updateSettings({ ...settings, shopAddress: e.target.value })}
                    placeholder="ناونیشانی پێشانگا"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ژمارە مۆبایل ١</Label>
                  <Input
                    value={settings.phone1}
                    onChange={(e) => updateSettings({ ...settings, phone1: e.target.value })}
                    placeholder="07xxxxxxxxx"
                    dir="ltr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ژمارە مۆبایل ٢</Label>
                  <Input
                    value={settings.phone2}
                    onChange={(e) => updateSettings({ ...settings, phone2: e.target.value })}
                    placeholder="07xxxxxxxxx"
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>تێبینی وەصڵ</Label>
                <Textarea
                  value={settings.receiptNote}
                  onChange={(e) => updateSettings({ ...settings, receiptNote: e.target.value })}
                  placeholder="ئەم تێبینییە لە خوارەوەی وەصڵەکان دەردەکەوێت..."
                  rows={2}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Theme & Invoice Number Cards - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Theme Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              شێواز
            </CardTitle>
            <CardDescription>شێوازی ڕووکار و ڕەنگ</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Theme Mode */}
              <div className="space-y-3">
                <Label>دۆخی ڕووکار</Label>
                <div className="flex gap-2">
                  <Button
                    variant={settings.theme === "light" ? "default" : "outline"}
                    onClick={() => updateSettings({ ...settings, theme: "light" })}
                    className="flex-1"
                  >
                    ڕووناک
                    <Sun className="h-4 w-4 mr-2" />
                  </Button>
                  <Button
                    variant={settings.theme === "dark" ? "default" : "outline"}
                    onClick={() => updateSettings({ ...settings, theme: "dark" })}
                    className="flex-1"
                  >
                    تاریک
                    <Moon className="h-4 w-4 mr-2" />
                  </Button>
                  <Button
                    variant={settings.theme === "system" ? "default" : "outline"}
                    onClick={() => updateSettings({ ...settings, theme: "system" })}
                    className="flex-1"
                  >
                    سیستەم
                    <Smartphone className="h-4 w-4 mr-2" />
                  </Button>
                </div>
              </div>

              {/* Primary Color */}
              <div className="space-y-3">
                <Label>ڕەنگی سەرەکی</Label>
                <div className="grid grid-cols-6 gap-3">
                  {COLORS.map((color) => {
                    const isDark = settings.theme === "dark" ||
                      (settings.theme === "system" && typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches);
                    const hsl = isDark ? color.hslDark : color.hslLight;
                    return (
                      <button
                        key={color.value}
                        onClick={() => updateSettings({ ...settings, primaryColor: color.value })}
                        className={`h-10 w-10 rounded-full border-2 cursor-pointer transition-all hover:scale-110 ${settings.primaryColor === color.value
                          ? "ring-2 ring-offset-2 ring-foreground scale-110"
                          : "border-border"
                          }`}
                        style={{ backgroundColor: `hsl(${hsl})` }}
                        title={color.name}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Number Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              ژمارەی وەصڵ
            </CardTitle>
            <CardDescription>بەڕێوەبردنی ژمارەی وەصڵی پارەدان</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Invoice Number Display */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
              <div className="flex-1">
                <Label className="text-sm font-medium text-muted-foreground">ژمارەی ئێستا</Label>
                <p className="text-3xl font-bold mt-1">
                  {isLoadingInvoice ? (
                    <span className="text-muted-foreground text-lg">چاوەڕوان بە...</span>
                  ) : (
                    currentInvoiceNumber !== null ? currentInvoiceNumber : "---"
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  داهاتوو: {currentInvoiceNumber !== null ? currentInvoiceNumber + 1 : "---"}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={fetchCurrentInvoiceNumber}
                disabled={isLoadingInvoice}
                size="sm"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>

            {/* Update Invoice Number */}
            <div className="space-y-2">
              <Label htmlFor="newInvoiceNumber" className="text-sm font-medium">
                دەستکاریکردن
              </Label>
              <div className="flex gap-2">
                <Input
                  id="newInvoiceNumber"
                  type="number"
                  value={newInvoiceNumber}
                  onChange={(e) => setNewInvoiceNumber(e.target.value)}
                  placeholder="ژمارەی نوێ"
                  dir="ltr"
                  className="text-center text-lg font-semibold"
                  disabled={isUpdatingInvoice}
                />
                <Button
                  onClick={updateInvoiceNumber}
                  disabled={isUpdatingInvoice || newInvoiceNumber === currentInvoiceNumber?.toString()}
                  className="shrink-0"
                >
                  {isUpdatingInvoice ? "..." : "نوێکردنەوە"}
                </Button>
              </div>
            </div>

            {/* Reset to Zero */}
            <div className="pt-2 border-t">
              <Button
                variant="destructive"
                onClick={resetInvoiceNumber}
                disabled={isResettingInvoice}
                className="w-full"
                size="sm"
              >
                {isResettingInvoice ? (
                  "چاوەڕوان بە..."
                ) : invoiceResetSuccess ? (
                  "سفر کرایەوە!"
                ) : (
                  <>
                    سفرکردنەوە بۆ ٠
                    <RotateCcw className="h-4 w-4 mr-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
